import { getDataVersionDownloadUrl } from '@/queries/dataset-data-versions';
import { formatRelativeTime, getErrorMessage } from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import RedoOutlined from '@ant-design/icons/RedoOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Modal from 'antd/es/modal';
import Table from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React from 'react';
import { useTheme } from 'styled-components';

import type { DatasetDataVersion } from '@mai/types';

const { Text } = Typography;

interface VersionHistoryModalProps {
  open: boolean;
  onCancel: () => void;
  title: string;
  versions: DatasetDataVersion[];
  datasetName: string;
  onMakeActive?: (versionId: string) => Promise<void>;
  loading?: boolean;
}

export const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  open,
  onCancel,
  title,
  versions,
  datasetName,
  onMakeActive,
  loading = false,
}) => {
  const { message } = App.useApp();
  const theme = useTheme();
  const handleDownload = async (version: DatasetDataVersion) => {
    try {
      const { downloadUrl } = await getDataVersionDownloadUrl(
        version.datasetDataVersionId,
      );

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${datasetName}-v${version.versionNo}.${version.fileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to download version');
      void message.error(errorMessage);
    }
  };

  const handleMakeActive = async (version: DatasetDataVersion) => {
    if (!onMakeActive) return;

    try {
      await onMakeActive(version.datasetDataVersionId);
      void message.success(`Version ${version.versionNo} is now active`);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to make version active',
      );
      void message.error(errorMessage);
    }
  };

  const columns: ColumnsType<DatasetDataVersion> = [
    {
      title: 'Version',
      dataIndex: 'versionNo',
      key: 'versionNo',
      width: 80,
      render: (versionNo: number) => (
        <Text
          style={{
            fontFamily: 'monospace',
            fontWeight: `${theme.fontWeightStrong}`,
          }}
        >
          v{versionNo}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        if (status === 'current') {
          return (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Active
            </Tag>
          );
        }
        return (
          <Tag color="default">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: Date | string) => {
        const parsedDate = typeof date === 'string' ? new Date(date) : date;
        return (
          <Text style={{ fontSize: `${theme.fontSize}px` }}>
            {formatRelativeTime(parsedDate)}
          </Text>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 80,
      render: (fileType: string) => (
        <Text
          style={{
            fontSize: `${theme.fontSize}px`,
            textTransform: 'uppercase',
          }}
        >
          {fileType}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: `${theme.marginXS}px` }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
          >
            Download
          </Button>
          {record.status !== 'current' && onMakeActive && (
            <Button
              size="small"
              type="text"
              icon={<RedoOutlined />}
              onClick={() => handleMakeActive(record)}
            >
              Make Active
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
      ]}
      width={800}
    >
      <Table
        columns={columns}
        dataSource={versions}
        rowKey="datasetDataVersionId"
        loading={loading}
        pagination={false}
        size="small"
        style={{ marginTop: `${theme.margin}px` }}
        scroll={{ x: 700 }}
      />
      {versions.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: `${theme.marginXL}px` }}>
          <Text type="secondary">No versions found</Text>
        </div>
      )}
    </Modal>
  );
};

export default VersionHistoryModal;
